// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ApplyFilterEvent} from 'common/components/filter-bar/types';
import {SelectionState} from 'common/components/items-menu/constants';
import {PropertySectionItem as PropertyItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {Section} from 'common/components/items-menu/types';
import {PropertySection as PropertiesMenuSection} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {getItemUniqueKey} from 'common/query-builder/query-block/properties-menu/util';
import {IronContextRegistry} from 'common/report/context-registry';

import {template} from './template';

interface UserPropertiesSelectorState {
  selected: PropertyItem[];
}

interface UserPropertiesSelectorJsxProps {
  attrs?: {
    'project-id'?: number;
    'data-group-id'?: string;
    theme?: string;
    'workspace-id'?: number;
    'selected-properties': PropertyItem[];
    'allow-behavioral-property-creation'?: boolean;
    'hide-edit-controls'?: boolean;
    'allow-zero-selections'?: boolean;
  };
  on?: {
    unselectAll?: (ev: UnselectAllEvent) => void;
    applyFilter?: (ev: ApplyFilterEvent) => void;
  };
}

type UnselectAllEvent = CustomEvent<{
  name: string;
  selectionState: SelectionState;
  previousSelected: Section[];
}>;

const name = `columns-query-block`;

export class UserPropertiesSelector extends Component<
  UserPropertiesSelectorState,
  UserPropertiesSelectorJsxProps[`attrs`],
  null,
  null,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<UserPropertiesSelectorJsxProps[`attrs`]> {
    return {
      ...super.attrsSchema,
      'project-id': `number`,
      'data-group-id': `string`,
      'selected-properties': `json`,
      theme: `string`,
      'workspace-id': `number`,
      'allow-behavioral-property-creation': `boolean`,
      'hide-edit-controls': `boolean`,
      'allow-zero-selections': `boolean`,
    };
  }

  get config() {
    return {
      template,
      defaultState: {
        selected: [],
      },
      contexts: [`currentProject` as const, `i18n` as const],
      helpers: {
        //TODO
        getSortedMenuSections: (sections: PropertiesMenuSection[]) => {
          const showingPropertiesHash: {[itemUniqueKey: string]: number} = {};
          this.attr(`selected-properties`).forEach((property, index) => {
            showingPropertiesHash[getItemUniqueKey(property)] = index;
          });

          const priority = (itemUniqueKey: string, isControlItem: boolean) => {
            if (isControlItem) {
              return 2;
            } else if (itemUniqueKey in showingPropertiesHash) {
              return 1;
            } else {
              return 0;
            }
          };

          return sections.map((section) => {
            const {items} = section;
            if (items.length > 0) {
              return {
                ...section,
                items: items.sort((a, b) => {
                  const aItemUniqueKey = getItemUniqueKey(a);
                  const bItemUniqueKey = getItemUniqueKey(b);

                  const aPriority = priority(aItemUniqueKey, a.isControlItem);
                  const bPriority = priority(bItemUniqueKey, b.isControlItem);

                  if (aPriority === bPriority && aItemUniqueKey in showingPropertiesHash) {
                    // negative if a lower than b, positive in other case
                    return showingPropertiesHash[aItemUniqueKey] - showingPropertiesHash[bItemUniqueKey];
                  } else if (aPriority === bPriority) {
                    const aVal = a.value;
                    const bVal = b.value;

                    const isEmptyArray =
                      (Array.isArray(aVal) && aVal.length === 0) || (Array.isArray(bVal) && bVal.length === 0);
                    if (isEmptyArray) {
                      return 1;
                    }
                    // value can be either string or string[]
                    // in case of string[] we use the last value as it is the value that is being displayed
                    const aValue = Array.isArray(aVal) ? aVal[aVal.length - 1] : aVal;
                    const bValue = Array.isArray(bVal) ? bVal[bVal.length - 1] : bVal;
                    return aValue.localeCompare(bValue);
                  } else if (aPriority > bPriority) {
                    return -1;
                  } else {
                    return 1;
                  }
                }),
              };
            }
            return section;
          });
        },
        handleQueryBlockSelect: (detail) => {
          const {item, rawItem, commit} = detail;

          if (commit) {
            this.dispatchEvent(
              new CustomEvent(`applyFilter`, {
                detail: {
                  name,
                  selected: item,
                },
              }),
            );
          } else {
            const selectedValues = this.state.selected;
            const alreadyExists = !!selectedValues.find((col) => col.value === rawItem.value);
            if (alreadyExists) {
              this.update({selected: selectedValues.filter((col) => col.value !== rawItem.value)});
            } else {
              this.update({selected: [...selectedValues, rawItem]});
            }
          }
        },
        handleQueryBlockClose: () => {
          this.updateSelected();
        },
        handleQueryBlockSelectAll: ({detail}: CustomEvent) => {
          this.update({selected: []});
          const previousSelected = this.getSelectedItems();
          this.dispatchEvent(
            new CustomEvent(`unselectAll`, {
              detail: {
                name: detail.name,
                selectionState: detail.selectorState,
                previousSelected,
              },
            }),
          );
        },
      },
    };
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `selected-properties`) {
      this.updateSelected();
    }
  }

  updateSelected() {
    const selected = this.attr(`selected-properties`).map((property) => ({
      ...property,
      resourceType: `user`,
      dataGroupId: this.attr(`data-group-id`),
    })) as PropertyItem[];
    this.update({selected});
  }

  getSelectedItems() {
    return this.state.selected.map(({value}) => value);
  }
}

export const UserPropertiesSelectorEl = defineAndMakeJsxFactory<UserPropertiesSelectorJsxProps>(
  `user-properties-selector`,
  UserPropertiesSelector,
);
export const UserPropertiesSelectorReact = panel2react<UserPropertiesSelectorJsxProps>(
  `user-properties-selector`,
  UserPropertiesSelector,
);
